import React from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import type { SpotlightStepConfig } from '../../types';

interface SpotlightStepProps {
  step: SpotlightStepConfig;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onDismiss: () => void;
}

const SpotlightStep: React.FC<SpotlightStepProps> = ({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onDismiss,
}) => {
  const theme = useTheme();
  const Icon = step.icon as React.ComponentType<SvgIconProps>;
  const isLastStep = stepIndex === totalSteps - 1;
  const isMultiStep = totalSteps > 1;

  // Determine action button label
  let actionLabel = step.actionLabel;
  if (!actionLabel) {
    if (!isMultiStep) {
      actionLabel = 'Got it';
    } else if (isLastStep) {
      actionLabel = 'Got it';
    } else {
      actionLabel = 'Next \u2192';
    }
  }

  return (
    <Box>
      {/* Header: icon + title + close */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: theme.palette.secondary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18, color: '#fff' }} />
        </Box>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, flex: 1, lineHeight: 1.3 }}
        >
          {step.title}
        </Typography>

        <IconButton
          size="small"
          onClick={onDismiss}
          aria-label="Close tour"
          sx={{
            ml: 'auto',
            flexShrink: 0,
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, mb: 2, lineHeight: 1.5 }}
      >
        {step.body}
      </Typography>

      {/* Footer: step dots + action button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
        {isMultiStep && (
          <Box sx={{ display: 'flex', gap: 0.75, mr: 'auto' }}>
            {Array.from({ length: totalSteps }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor:
                    i === stepIndex
                      ? theme.palette.secondary.main
                      : theme.palette.action.disabled,
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </Box>
        )}

        <Button
          variant="contained"
          size="small"
          onClick={onNext}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            borderRadius: '8px',
            px: 2,
            py: 0.5,
            backgroundColor: theme.palette.secondary.main,
            '&:hover': { backgroundColor: theme.palette.secondary.dark },
          }}
        >
          {actionLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default SpotlightStep;
